import React from "react"
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
} from "react-simple-maps"

import { useTheme, resolveCategoryColor } from "../../utils/theme"
import { EXCLUDED_COUNTRY_CODES } from "../../utils/continents"
import { DEFAULT_MAP_VIEW } from "../../utils/mapConfig"

const geoUrl =
  "https://raw.githubusercontent.com/holtzy/D3-graph-gallery/master/DATA/world.geojson";

const MapRenderer = ({
  position,
  selectedRegion,
  setTooltip,
  setHoverCountry,
  selectRegion,
  handleMoveEnd,
  hoveredRegion,
  setHoverRegion,
  countryToRegion,
  availableCountries,
  regionsMap,
  onCountryClick,
}) => {
  const { colors } = useTheme()
  const width = 960
  const height = 600

  const isRegionMode = selectedRegion === null;

  return (
    <ComposableMap
      projection="geoMercator"
      width={width}
      height={height}
      className="world-map-svg"
    >
      <ZoomableGroup
        center={position.coordinates}
        zoom={position.zoom}
        minZoom={DEFAULT_MAP_VIEW.zoom}
        translateExtent={[
          [-100, -50],
          [width + 100, height + 50],
        ]}
        onMoveEnd={handleMoveEnd}
      >
        <Geographies geography={geoUrl}>
          {({ geographies }) =>
            geographies
              .filter((geo) => !EXCLUDED_COUNTRY_CODES.has(geo.id))
              .map((geo) => {
              const name = geo.properties.name
              const code = geo.id;
              const region = countryToRegion[code];

              const isKnownCountry = Boolean(region)

              const isHoveredRegion =
                isRegionMode && hoveredRegion === region
              const isSelectedRegion =
                selectedRegion === region

              const isInteractive =
                (isKnownCountry && (isRegionMode || region === selectedRegion))

              // -----------------------------
              // Fill color
              // -----------------------------
              let fillColor
              if (!isKnownCountry) {
                fillColor = colors.country // unknown countries
              } else if (!isRegionMode && selectedRegion === region) {
                const availableList = availableCountries[selectedRegion] || []
                fillColor = availableList.includes(code)
                  ? colors.availableCountry
                  : colors.unavailableCountry
              } else {
                fillColor = resolveCategoryColor(colors.regions, region, regionsMap[region]?.color) || colors.country;
              }

              // -----------------------------
              // Opacity (spotlight effect)
              // -----------------------------
              let opacity = 1
              if (isRegionMode && hoveredRegion) {
                opacity = isHoveredRegion ? 1 : 0.5
              } else if (!isRegionMode) {
                opacity = isSelectedRegion ? 1 : 0.35
              }

              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  onMouseEnter={(e) => {
                    if (!isInteractive) return

                    if (isRegionMode) {
                      setHoverRegion(region, false)
                      setHoverCountry(null, false)
                      setTooltip({
                        visible: true,
                        name: regionsMap[region]?.name || region,
                        x: e.clientX,
                        y: e.clientY,
                      })
                    } else {
                      setHoverCountry(code, false)
                      setHoverRegion(null, false)
                      setTooltip({ visible: true, name, x: e.clientX, y: e.clientY })
                    }
                  }}
                onMouseMove={(e) => {
                  if (!isInteractive) return
                  setTooltip((prev) => ({ ...prev, x: e.clientX, y: e.clientY }))
                }}
                onMouseLeave={() => {
                  if (isRegionMode) {
                    setHoverRegion(null, false)
                  } else {
                    setHoverCountry(null, false)
                  }
                  setTooltip({ visible: false })
                }}
                onContextMenu={(e) => {
                  e.preventDefault()
                  if (isRegionMode) {
                    setHoverRegion(region, true)  // locks region
                  } else {
                    setHoverCountry(code, true) // locks country
                  }
                }}
                onClick={() => {
                  if (isRegionMode && region) selectRegion(region)
                  else if (!isRegionMode && isInteractive) {
                    onCountryClick(code)
                  }
                }}
                  style={{
                    default: {
                      fill: fillColor,
                      opacity: opacity,
                      stroke: colors.countryBorder,
                      strokeWidth: 0.5,
                      transition: "all 0.25s ease",
                      outline: "none",
                      cursor: isInteractive ? "pointer" : "default",
                      filter:
                        isRegionMode && isHoveredRegion
                          ? "brightness(1.15)"
                          : "none",
                    },
                    hover: {
                      fill: fillColor,
                      opacity: opacity,
                      stroke: colors.countryBorder,
                      strokeWidth: 0.5,
                      outline: "none",
                      cursor: isInteractive ? "pointer" : "default",
                      filter:
                        isInteractive && isRegionMode && isHoveredRegion
                          ? "brightness(1.15)"
                          : isInteractive && !isRegionMode && region === selectedRegion
                          ? "brightness(1.1)"
                          : "none",
                    },
                    pressed: {
                      fill: fillColor,
                      outline: "none",
                    },
                  }}
                />
              )
            })
          }
        </Geographies>
      </ZoomableGroup>
    </ComposableMap>
  )
}

export default MapRenderer
